import * as React from 'react';
import { Button, Menu, MenuTrigger, Dropdown, Option, Input, TableCellLayout, TableColumnDefinition, Text, ToolbarButton, createTableColumn, makeStyles, useId, InfoLabel, Subtitle1, Subtitle2, TableRowId, Checkbox, Field, InputOnChangeData, OptionOnSelectData, CheckboxOnChangeData, SplitButton, MenuButtonProps, MenuPopover, MenuItem, MenuList, Spinner, Tooltip, MessageBar, MessageBarBody } from '@fluentui/react-components';
import {
    Save24Regular,
    Add24Regular,
    Delete24Regular,
    PresenceBlockedRegular,
    OpenRegular
} from "@fluentui/react-icons";
import { ApiKeyReference, EntityScope, Membership, Publisher } from '../../dto/Publisher';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { GenericToolbar } from '../../components/GenericToolbar';
import { formStyle, formInputStyle, flexRow, flexColumn, formInputInlineStyle, infoLabel, flexGrow, flexRowWrap } from '../../styles/GlobalStyles';
import { useGlobalContext } from '../../state/GlobalStateProvider';
import { GenericGrid } from '../../components/GenericGrid';
import { CopyButton } from '../../components/CopyButton';
import { FluentDialog } from '../../components/FluentDialog';
import { FormBase } from "./FormBase";
import { fetchRecord, submit } from '../../util/FormUtils';
import { ResolveUserResponse } from '../../dto/ResolveUserResponse';
import { v4 as uuidv4 } from 'uuid';
import { RecordReference } from '../../dto/RecordReference';
import { ensureSuccess, handleError } from '../../util/ErrorHandling';
import { ServiceTier, usePaddlePriceId } from '../../util/PaddleEnv';
import { SubscriptionInformation } from '../../dto/SubscriptionInformation';
import { CenteredContent } from '../../components/CenteredContent';
import { RecordType } from '../../dto/RecordType';

const entitySet = "publishers";

const memberRowSelector = (item: Membership) => item.guid;
const apiKeyRowSelector = (item: ApiKeyReference) => item.guid;

const defaultScope: EntityScope = { read: false, write: false };

const scopeToOptions = (scope: EntityScope): Permission[] => {
    const result: Permission[] = [];
    if (scope.read) result.push("read");
    if (scope.write) result.push("write");
    return result;
};

const optionsToScope = (options: Permission[]): EntityScope => ({
    read: options.includes("read"),
    write: options.includes("write")
});

type Permission = "read" | "write";

const useStyles = makeStyles({
    formInput: formInputStyle,
    formInputInline: formInputInlineStyle,
    form: formStyle,
    flexGrow: flexGrow,
    flexRow: flexRow,
    flexRowWrap: flexRowWrap,
    flexColumn: flexColumn,
    infoLabel: infoLabel,
    tierCardContainer: {
        ...flexRow,
        justifyContent: "center",
        flexWrap: "wrap"
    },
    tierCard: {
        ...flexColumn,
        padding: "10px",
        borderRadius: "5px",
        flex: "1 0 0",
        minWidth: "200px",
        position: "relative"
    },
    purchaseButton: {
        position: "absolute",
        top: "10px",
        right: "10px"
    }
});

export const PublisherForm: React.FC<any> = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { publisherId } = useParams();
    const priceIdDictionary = usePaddlePriceId();

    const [globalState, globalDispatch] = useGlobalContext();
    const [isSaving, setIsSaving] = React.useState(false);
    const [record, setRecord] = React.useState<Publisher>({} as Publisher);
    const isEditable = React.useMemo(() => !record.guid || ["contributor", "admin"].includes(record.currentUserAccessLevel), [ record ]);
    const isSaveDisabled = React.useMemo(() => isSaving || !record.name || !isEditable, [isEditable, isSaving, record.name]);

    // Member state
    const [selectedMembers, setSelectedMembers] = React.useState<TableRowId[]>([]);
    const [isAddingMember, setIsAddingMember] = React.useState(false);
    const [memberId, setMemberId] = React.useState("");
    const [member, setMember] = React.useState<RecordReference | null>(null);
    const [memberType, setMemberType] = React.useState("");
    const [memberNotFound, setMemberNotFound] = React.useState(false);

    // API Key state
    const [selectedApiKeys, setSelectedApiKeys] = React.useState<TableRowId[]>([]);
    const [isAddingApiKey, setIsAddingApiKey] = React.useState(false);
    const [apiKeyId, setApiKeyId] = React.useState("");
    const [apiKeyName, setApiKeyName] = React.useState("");
    const [customerScope, setCustomerScope] = React.useState<EntityScope>(defaultScope);
    const [licenseScope, setLicenseScope] = React.useState<EntityScope>(defaultScope);
    const [productScope, setProductScope] = React.useState<EntityScope>(defaultScope);
    const [telemetryScope, setTelemetryScope] = React.useState<EntityScope>(defaultScope);

    // Service tier state
    const [isChangingServiceTier, setIsChangingServiceTier] = React.useState(false);
    const [isProcessingServiceTierChange, setIsProcessingServiceTierChange] = React.useState(false);
    const [newServiceTier, setNewServiceTier] = React.useState("");

    const styles = useStyles();
    const selfServiceSpanId = useId("selfservice");

    const memberColumns: TableColumnDefinition<Membership>[] = React.useMemo(() => [
        createTableColumn<Membership>({
            columnId: "Member",
            compare: (a, b) => {
                return a.member.name.localeCompare(b.member.name);
            },
            renderHeaderCell: () => {
                return "Member";
            },
            renderCell: (item) => {
                return (
                    <TableCellLayout truncate>
                        {item.member.name}
                    </TableCellLayout>
                );
            },
        }),
        createTableColumn<Membership>({
            columnId: "AccessLevel",
            compare: (a, b) => {
                return a.accessLevel.localeCompare(b.accessLevel);
            },
            renderHeaderCell: () => {
                return "Access Level";
            },
            renderCell: (item) => {
                return (
                    <TableCellLayout truncate>
                        {item.accessLevel}
                    </TableCellLayout>
                );
            },
        })
    ], []);

    const apiKeyColumns: TableColumnDefinition<ApiKeyReference>[] = React.useMemo(() => [
        createTableColumn<ApiKeyReference>({
            columnId: "Name",
            compare: (a, b) => {
                return (a.name ?? "").localeCompare(b.name ?? "");
            },
            renderHeaderCell: () => {
                return "Name";
            },
            renderCell: (item) => {
                return (
                    <TableCellLayout truncate>
                        {item.name}
                    </TableCellLayout>
                );
            },
        }),
        createTableColumn<ApiKeyReference>({
            columnId: "Fingerprint",
            compare: (a, b) => {
                return (a.apiKeyId?.name ?? "").localeCompare(b.apiKeyId?.name ?? "");
            },
            renderHeaderCell: () => {
                return "Fingerprint";
            },
            renderCell: (item) => {
                return (
                    <TableCellLayout truncate>
                        {item.apiKeyId?.name}
                    </TableCellLayout>
                );
            },
        }),
        createTableColumn<ApiKeyReference>({
            columnId: "API Key",
            compare: (a, b) => {
                return (a.apiKeyDisplay ?? "").localeCompare(b.apiKeyDisplay ?? "");
            },
            renderHeaderCell: () => {
                return "API Key";
            },
            renderCell: (item) => {
                return (
                    <TableCellLayout truncate>
                        <Input type="text" value={item.apiKeyDisplay ?? ""} disabled contentAfter={<CopyButton disabled={!item.apiKeyDisplay} value={item.apiKeyDisplay} />} />
                    </TableCellLayout>
                );
            },
        }),
        createTableColumn<ApiKeyReference>({
            columnId: "State",
            compare: (a, b) => {
                return (a.state ?? "").localeCompare(b.state ?? "");
            },
            renderHeaderCell: () => {
                return "State";
            },
            renderCell: (item) => {
                return (
                    <TableCellLayout truncate>
                        {item.state}
                    </TableCellLayout>
                );
            },
        })
    ], []);

    const cancelMemberDialog = React.useCallback(() =>
    {
        setMember(null);
        setMemberId("");
        setMemberType("");
        setIsAddingMember(false);
        setMemberNotFound(false);
    }, []);

    const submitMemberDialog = React.useCallback(async () =>
    {
        if (memberId)
        {
            setRecord({
                ...record,
                users: (record.users ?? []).map(u => u.guid === memberId ? ({...u, accessLevel: memberType}) : u)
            });

            cancelMemberDialog();
        }
        else {
            try
            {
                const response = await fetch("/api/resolveuser", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },        
                    body: JSON.stringify({ emailAddress: member?.name })
                });

                await ensureSuccess(response);

                const resolvedUser = await response.json() as ResolveUserResponse;
                
                if (resolvedUser.userReference != null)
                {
                    setRecord({
                        ...record,
                        users: [
                            ...record.users,
                            {
                                guid: uuidv4(),
                                accessLevel: memberType,
                                member: resolvedUser.userReference
                            }
                        ]
                    });

                    cancelMemberDialog();
                }
                else
                {
                    setMemberNotFound(true);
                }
            }
            catch (e) {
                setMemberNotFound(true);

                handleError(e as Error, globalDispatch);
            }
        }
    }, [memberId, record, cancelMemberDialog, memberType, member?.name, globalDispatch]);

    const editMember = React.useCallback((userId: TableRowId) =>
    {
        const member = record.users.find(u => u.guid === userId.toString());

        if (member)
        {
            setMemberId(member.guid);
            setMember(member.member);
            setMemberType(member.accessLevel);
            setIsAddingMember(true);
        }
    }, [record.users]);

    const onMemberSelection = React.useCallback((selection: TableRowId[]) =>
    {
        setSelectedMembers(selection);
    }, []);

    const onMemberDeletion = React.useCallback(() =>
    {
        setRecord({
            ...record,
            users: [
                ...(record.users ?? []).filter(u => !selectedMembers.includes(u.guid))]
        });
    }, [record, selectedMembers]);

    const onMemberNameChange = React.useCallback((ev: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) =>
    {
        setMemberNotFound(false);
        setMember({ guid: "", type: "user", name: data.value });
    }, []);

    const onMemberTypeChange = React.useCallback((event: any, data: OptionOnSelectData) =>
    {
        setMemberType(data.optionValue!);
    }, []);

    // API Key handlers
    const cancelApiKeyDialog = React.useCallback(() =>
    {
        setApiKeyId("");
        setApiKeyName("");
        setCustomerScope(defaultScope);
        setLicenseScope(defaultScope);
        setProductScope(defaultScope);
        setTelemetryScope(defaultScope);
        setIsAddingApiKey(false);
    }, []);

    const submitApiKeyDialog = React.useCallback(() =>
    {
        const scopes: Partial<Record<RecordType, EntityScope>> = ({
            telemetry: telemetryScope,
            product: productScope,
            customer: customerScope,
            license: licenseScope
        });

        if (apiKeyId)
        {
            // Edit existing
            setRecord({
                ...record,
                apiKeys: (record.apiKeys ?? []).map(k => k.guid === apiKeyId 
                    ? { ...k, name: apiKeyName, scopes } 
                    : k
                )
            });
        }
        else
        {
            // Add new
            const newApiKey: ApiKeyReference = {
                guid: uuidv4(),
                name: apiKeyName,
                scopes
            };
            setRecord({ 
                ...record, 
                apiKeys: [...(record.apiKeys ?? []), newApiKey] 
            });
        }

        cancelApiKeyDialog();
    }, [apiKeyId, apiKeyName, telemetryScope, productScope, customerScope, licenseScope, record, cancelApiKeyDialog]);

    const editApiKey = React.useCallback((keyId: TableRowId) =>
    {
        const apiKey = record.apiKeys?.find(k => k.guid === keyId.toString());

        if (apiKey)
        {
            setApiKeyId(apiKey.guid);
            setApiKeyName(apiKey.name ?? "");

            setCustomerScope(apiKey.scopes?.customer ?? defaultScope);
            setLicenseScope(apiKey.scopes?.license ?? defaultScope);
            setProductScope(apiKey.scopes?.product ?? defaultScope);
            setTelemetryScope(apiKey.scopes?.telemetry ?? defaultScope);
            
            setIsAddingApiKey(true);
        }
    }, [record.apiKeys]);

    const onApiKeySelection = React.useCallback((selection: TableRowId[]) =>
    {
        setSelectedApiKeys(selection);
    }, []);

    const onApiKeyRevokation = React.useCallback(() =>
    {
        setRecord({
            ...record,
            apiKeys: (record.apiKeys ?? []).map(k => !selectedApiKeys.includes(k.guid) ? k : ({...k, state: "inactive"}))
        });
    }, [record, selectedApiKeys]);

    const onApiKeyNameChange = React.useCallback((ev: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) =>
    {
        setApiKeyName(data.value);
    }, []);

    const onCustomerScopeChange = React.useCallback((event: any, data: OptionOnSelectData) =>
    {
        setCustomerScope(optionsToScope(data.selectedOptions as Permission[]));
    }, []);

    const onLicenseScopeChange = React.useCallback((event: any, data: OptionOnSelectData) =>
    {
        setLicenseScope(optionsToScope(data.selectedOptions as Permission[]));
    }, []);

    const onProductScopeChange = React.useCallback((event: any, data: OptionOnSelectData) =>
    {
        setProductScope(optionsToScope(data.selectedOptions as Permission[]));
    }, []);

    const onTelemetryScopeChange = React.useCallback((event: any, data: OptionOnSelectData) =>
    {
        setTelemetryScope(optionsToScope(data.selectedOptions as Permission[]));
    }, []);

    const onNameChange = React.useCallback((ev: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) =>
    {
        setRecord((record) => ({...record, name: data.value}));
    }, []);

    const cancelServiceTierDialog = React.useCallback(() =>
    {
        setIsChangingServiceTier(false);
        setIsProcessingServiceTierChange(false);
        setNewServiceTier("");
    }, []);

    const onServiceTierChange = React.useCallback((event: any, data: OptionOnSelectData) =>
    {
        setNewServiceTier(data.optionValue!);
    }, []);

    const submitServiceTierDialog = React.useCallback(async () =>
    {
        try
        {
            setIsProcessingServiceTierChange(true);

            const priceId = priceIdDictionary[newServiceTier as ServiceTier];

            if (priceId)
            {
                const response = await fetch(`/api/publishers/${publisherId}/subscription`, {
                    method: "PATCH",
                    headers: {
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify({
                        priceId
                    })
                });
        
                await ensureSuccess(response);
        
                let updatedRecord: Publisher | undefined;
                
                do
                {
                    await new Promise((resolve) => setTimeout(resolve, 2000));
                    updatedRecord = await fetchRecord<Publisher>(publisherId, publisherId, entitySet, setRecord, globalDispatch);
                } while (updatedRecord?.serviceTier !== newServiceTier)
            }
        }
        catch(e)
        {
            handleError(e as Error, globalDispatch);
        }
        finally
        {
            cancelServiceTierDialog();
        }
    }, [cancelServiceTierDialog, globalDispatch, newServiceTier, priceIdDictionary, publisherId]);

    const openServiceTierDialog = React.useCallback(async () =>
    {
        setIsChangingServiceTier(true);
    }, []);

    const onLandingPageEnabledChange = React.useCallback((ev: React.ChangeEvent<HTMLInputElement>, data: CheckboxOnChangeData) =>
    {
        setRecord((record) => ({...record, landingPageEnabled: data.checked as boolean}));
    }, []);

    const didInitRef = React.useRef<string | null>(null);

    // This is the initialization hook
    React.useEffect(() =>
    {
        // If we already initialized once, just exit, updates supply us with the latest record state automatically
        if (didInitRef.current !== publisherId)
        {
            if (!publisherId)
            {
                return;
            }

            // This happens right after the transition from id === "new" to the actual id
            if (location?.state?.record?.guid === publisherId)
            {
                // Use record from location once...
                setRecord(location.state.record);

                //...then discard so that it can never overwrite local edits
                navigate(location.pathname, {
                    replace: true,
                    state: null
                });
            }
            else
            {
                // In all other cases, just fetch
                fetchRecord(
                    publisherId,
                    publisherId,
                    entitySet,
                    setRecord,
                    globalDispatch
                );
            }

            didInitRef.current = publisherId;
        }
    },
    [
        publisherId,
        navigate,
        location.pathname,
        location.state,
        globalDispatch
    ]);

    const dialogs = React.useMemo(() => (
        <>
            <FluentDialog open={isAddingMember} title="Add Member" submitDisabled={!member?.name || !memberType || memberNotFound || !isEditable} onSubmit={submitMemberDialog} onCancel={cancelMemberDialog}>
                <div className={styles.form}>
                    <Field
                        label="E-Mail Address"
                        hint={`${(member?.name ?? "").length} / 254`}
                    >
                        <Input maxLength={254} disabled={!!member?.guid} type="text" value={member?.name ?? ""} onChange={onMemberNameChange} />
                    </Field>

                    <Field
                        label="Member Type"
                    >
                        <Dropdown
                            placeholder="Choose type for new member"
                            defaultValue={memberType ? memberType : undefined}
                            defaultSelectedOptions={memberType ? [memberType] : undefined}
                            onOptionSelect={onMemberTypeChange}
                        >
                            <Option key="reader" value="reader">Reader</Option>
                            <Option key="contributor" value="contributor">Contributor</Option>
                            <Option key="admin" value="admin">Admin</Option>
                        </Dropdown>
                    </Field>

                    { memberNotFound &&
                        <div className={styles.formInput} style={{ color: "red" }}>
                            <Text>
                                User not known, or not publicly visible, or your login domain is not on user's trusted domain list.
                            </Text>
                        </div>
                    }
                </div>
            </FluentDialog>

            <FluentDialog 
                open={isAddingApiKey} 
                title={`${apiKeyId ? "Edit" : "Add"} API Key`} 
                submitDisabled={!apiKeyName || !isEditable || ![telemetryScope, productScope, customerScope, licenseScope].some(s => s.read || s.write)} 
                onSubmit={submitApiKeyDialog} 
                onCancel={cancelApiKeyDialog}
            >
                <div className={styles.form}>
                    { !apiKeyId  && (
                        <MessageBar intent="info">
                            <MessageBarBody>
                                Be sure to copy the API key and save it in a secure location after saving the form, it will only ever be visible once!
                            </MessageBarBody>
                        </MessageBar> )
                    }

                    <Field
                        label="Name"
                        hint={`${(apiKeyName ?? "").length} / 200`}
                        required
                    >
                        <Input maxLength={200} type="text" value={apiKeyName ?? ""} onChange={onApiKeyNameChange} />
                    </Field>

                    <Field
                        label={
                            <InfoLabel info="Control access to customer-level operations">
                                Customer Scope
                            </InfoLabel>
                        }
                        >
                        <Dropdown
                            multiselect
                            placeholder="Choose permissions"
                            value={`${customerScope?.read ? "r" : ""}${customerScope?.write ? "w" : ""}`}
                            selectedOptions={scopeToOptions(customerScope)}
                            onOptionSelect={onCustomerScopeChange}
                        >
                            <Option key="read" value="read">Read</Option>
                            <Option key="write" value="write">Write</Option>
                        </Dropdown>
                    </Field>

                    <Field
                        label={
                            <InfoLabel info="Control access to license-level operations">
                                License Scope
                            </InfoLabel>
                        }
                        >
                        <Dropdown
                            multiselect
                            placeholder="Choose permissions"
                            value={`${licenseScope?.read ? "r" : ""}${licenseScope?.write ? "w" : ""}`}
                            selectedOptions={scopeToOptions(licenseScope)}
                            onOptionSelect={onLicenseScopeChange}
                        >
                            <Option key="read" value="read">Read</Option>
                            <Option key="write" value="write">Write</Option>
                        </Dropdown>
                    </Field>

                    <Field
                        label={
                            <InfoLabel info="Control access to product-level operations">
                                Product Scope
                            </InfoLabel>
                        }
                        >
                        <Dropdown
                            multiselect
                            placeholder="Choose permissions"
                            value={`${productScope?.read ? "r" : ""}${productScope?.write ? "w" : ""}`}
                            selectedOptions={scopeToOptions(productScope)}
                            onOptionSelect={onProductScopeChange}
                        >
                            <Option key="read" value="read">Read</Option>
                            <Option key="write" value="write">Write</Option>
                        </Dropdown>
                    </Field>

                    <Field
                        label={
                            <InfoLabel info="Control access to telemetry-level operations. Only read is possible using API keys, maintain and use telemetry keys on the product where you want to send telemetry.">
                                Telemetry Scope
                            </InfoLabel>
                        }
                        >
                        <Dropdown
                            multiselect
                            placeholder="Choose permissions"
                            value={`${telemetryScope?.read ? "r" : ""}${telemetryScope?.write ? "w" : ""}`}
                            selectedOptions={scopeToOptions(telemetryScope)}
                            onOptionSelect={onTelemetryScopeChange}
                        >
                            <Option key="read" value="read">Read</Option>
                            <Option key="write" value="write" disabled>Write</Option>
                        </Dropdown>
                    </Field>

                    <Text size={200} style={{ fontStyle: "italic", marginTop: "8px" }}>
                        Note: At least one scope must be set to something other than "None"
                    </Text>
                </div>
            </FluentDialog>

            <FluentDialog open={isChangingServiceTier} title="Change Service Tier" submitDisabled={!newServiceTier || newServiceTier === record?.serviceTier || isProcessingServiceTierChange || !isEditable} submitLabel='Confirm Purchase (Prorated)' onSubmit={submitServiceTierDialog} onCancel={cancelServiceTierDialog} hideCancel={isProcessingServiceTierChange}>
                { !isProcessingServiceTierChange && (
                    <div className={styles.form}>
                        <Field label="New service tier">
                            <Dropdown
                                placeholder="Choose new service tier"
                                defaultValue={record?.serviceTier ? `${record.serviceTier[0].toUpperCase()}${record.serviceTier.substring(1)}` : ""}
                                defaultSelectedOptions={record?.serviceTier ? [record.serviceTier] : undefined}
                                onOptionSelect={onServiceTierChange}
                            >
                                <Option key="basic" value="basic">Basic</Option>
                                <Option key="premium" value="premium">Premium</Option>
                                <Option key="enterprise" value="enterprise">Enterprise</Option>
                            </Dropdown>
                        </Field>
                        <Text>By confirming this change, you agree that your subscription will be updated immediately. You may be charged or refunded a prorated amount based on your new plan. Future billing will reflect the updated plan rate unless you cancel before the next renewal date. For switching back to trial, please cancel your subscription.</Text>
                    </div> )
                }
                { isProcessingServiceTierChange && <CenteredContent><Spinner /></CenteredContent>}
            </FluentDialog>
        </>
    ), [isAddingMember, member?.name, member?.guid, memberType, memberNotFound, isEditable, submitMemberDialog, cancelMemberDialog, styles.form, styles.formInput, onMemberNameChange, onMemberTypeChange, isAddingApiKey, apiKeyId, apiKeyName, telemetryScope, productScope, customerScope, licenseScope, submitApiKeyDialog, cancelApiKeyDialog, onApiKeyNameChange, onTelemetryScopeChange, onProductScopeChange, onCustomerScopeChange, onLicenseScopeChange, isChangingServiceTier, newServiceTier, record.serviceTier, isProcessingServiceTierChange, submitServiceTierDialog, cancelServiceTierDialog, onServiceTierChange]);

    const submitAndSet = React.useCallback(async () =>
    {
        if (!isSaveDisabled)
        {
            const upsertData = await submit(publisherId === "new" ? undefined : publisherId, publisherId, record, entitySet, setRecord, setIsSaving, navigate, globalDispatch);

            if (upsertData)
            {
                if(globalState.publishers)
                {
                    if(!globalState.publishers.some(o => o.guid === upsertData.guid))
                    {
                        globalDispatch({ type: "setPublishers", payload: [...(globalState.publishers ?? []), upsertData] });
                    }
                    else
                    {
                        globalDispatch({ type: "setPublishers", payload: globalState.publishers.map(o => o.guid === upsertData.guid ? upsertData : o) });
                    }
                }
            }
        }
    }, [globalDispatch, globalState.publishers, isSaveDisabled, navigate, publisherId, record]);

    const toolbar = React.useMemo(() => (
        <GenericToolbar
            actionButtons={
                <>
                    <Tooltip relationship='label' content="Save (Ctrl + S)">
                        <ToolbarButton
                            aria-label="Save"
                            appearance="primary"
                            icon={<Save24Regular />}
                            disabled={isSaveDisabled}
                            onClick={submitAndSet}
                        />
                    </Tooltip>
                </>
            }
            farGroup={
                <>
                    <Field
                        label={
                            <InfoLabel size="medium" className={styles.infoLabel} info="External representation of this record's id. Visible on generated licenses.">
                                External Id
                            </InfoLabel>
                        }
                    >
                        <Input type="text" value={record.externalId ?? ""} disabled contentAfter={<CopyButton disabled={!record.externalId} value={record.externalId} />} />
                    </Field>
                </>
            }
        />
    ), [isSaveDisabled, submitAndSet, styles.infoLabel, record.externalId]);

    const onUpdatePaymentMethod = React.useCallback( async () =>
    {
        try
        {
            const response = await fetch(`/api/publishers/${publisherId}/subscription`, {
                headers: {
                    "Content-Type": "application/json"
                }
            });
    
            await ensureSuccess(response);
    
            const body = await response.json() as SubscriptionInformation;

            if (body?.managementUrls?.updatePaymentMethod)
            {
                window.open(body.managementUrls.updatePaymentMethod, "_blank");
            }
        }
        catch(e)
        {
            handleError(e as Error, globalDispatch);
        }
    }, [globalDispatch, publisherId]);

    const onCancel = React.useCallback( async () =>
    {
        try
        {
            const response = await fetch(`/api/publishers/${publisherId}/subscription`, {
                headers: {
                    "Content-Type": "application/json"
                }
            });
    
            await ensureSuccess(response);
    
            const body = await response.json() as SubscriptionInformation;

            if (body?.managementUrls?.cancel)
            {
                window.open(body.managementUrls.cancel, "_blank");
            }
        }
        catch(e)
        {
            handleError(e as Error, globalDispatch);
        }
    }, [globalDispatch, publisherId]);

    const activeTierButton = React.useMemo(() => (
        <Menu positioning="below-end">
            <MenuTrigger disableButtonEnhancement>
            {(triggerProps: MenuButtonProps) => (
                <SplitButton
                    menuButton={triggerProps}
                    size="small"
                    appearance="primary"
                    className={styles.purchaseButton}
                >
                    Active
                </SplitButton>
            )}
            </MenuTrigger>

            <MenuPopover>
                <MenuList>
                    <MenuItem disabled={record?.currentUserAccessLevel !== "admin"} onClick={openServiceTierDialog}>Change Service Tier</MenuItem>
                    <MenuItem disabled={record?.currentUserAccessLevel !== "admin"} onClick={onUpdatePaymentMethod}>Update Payment Method</MenuItem>
                    <MenuItem disabled={record?.currentUserAccessLevel !== "admin"} onClick={onCancel}>Cancel</MenuItem>
                </MenuList>
            </MenuPopover>
      </Menu>
    ), [onCancel, onUpdatePaymentMethod, openServiceTierDialog, record?.currentUserAccessLevel, styles.purchaseButton])

    const body = React.useMemo(() => (
        <>
            <Field
                label={
                    <InfoLabel size="medium" required className={styles.infoLabel} info="Publisher name. Visible on generated licenses.">
                        Name
                    </InfoLabel>
                }
                hint={`${(record.name ?? "").length} / 200`}
            >
                <Input maxLength={200} type="text" value={record.name ?? ""} disabled={!isEditable} onChange={onNameChange} />
            </Field>

            <Field
                label={
                    <InfoLabel size="medium" className={styles.infoLabel} info="Enables or disables a landing page where your customers can see and get trials for your trial-enabled products (Enterprise service tier feature).">
                        Landing Page Enabled
                    </InfoLabel>
                }
            >
                <Checkbox checked={record.landingPageEnabled ?? false} disabled={!isEditable} onChange={onLandingPageEnabledChange} />
            </Field>

            { record.guid && record.guid !== "new" && record.landingPageEnabled &&
                <Field
                    label={
                        <InfoLabel size="medium" className={styles.infoLabel} info="Landing page where your customers can see and get trials for your trial-enabled products (Enterprise service tier feature).">
                            Public Landing Page
                        </InfoLabel>
                    }
                >
                    <Input type="text" value={`${window.location.origin}/public/publishers/${record.externalId}`} disabled contentAfter={<div className={styles.flexRow}><Button size="small" disabled={!record.externalId} icon={<OpenRegular />} onClick={() => window.open(`${window.location.origin}/public/publishers/${record.externalId}`, "_blank")}></Button><CopyButton disabled={!record.externalId} value={`${window.location.origin}/public/publishers/${record.externalId}`} /></div>} />
                </Field>
            }
                
            { !!record.guid &&
                <div className={styles.flexColumn} style={{ borderRadius: "5px", marginTop: "20px" }}>
                    <InfoLabel size="medium" className={styles.infoLabel} info="Use this for adding colleagues to this publisher to be able to collaborate">
                        <Subtitle1>Members</Subtitle1>
                    </InfoLabel>

                    <GenericGrid
                        onRowClick={editMember}
                        columns={memberColumns}
                        items={record?.users ?? []}
                        onSelectionChange={onMemberSelection}
                        rowSelector={memberRowSelector}
                        actionButtons={
                            <>
                                <ToolbarButton
                                    aria-label="Invite Members"
                                    appearance="primary"
                                    icon={<Add24Regular />}
                                    disabled={!isEditable}
                                    onClick={() => setIsAddingMember(true)}
                                />
                                <ToolbarButton
                                    aria-label="Delete record"
                                    icon={<Delete24Regular />}
                                    disabled={!isEditable}
                                    onClick={onMemberDeletion}
                                />
                            </>
                        }
                    />
                </div>
            }

            { !!record.guid &&
                <div className={styles.flexColumn} style={{ borderRadius: "5px", marginTop: "20px" }}>
                    <InfoLabel size="medium" className={styles.infoLabel} info="Create API keys for being able to programmatically contact Ianus Guard REST APIs. Only available for premium and enterprise publishers.">
                        <Subtitle1>API Keys</Subtitle1>
                    </InfoLabel>

                    <GenericGrid
                        onRowClick={editApiKey}
                        columns={apiKeyColumns}
                        items={record?.apiKeys ?? []}
                        onSelectionChange={onApiKeySelection}
                        rowSelector={apiKeyRowSelector}
                        actionButtons={
                            <>
                                <ToolbarButton
                                    aria-label="Add New"
                                    appearance="primary"
                                    icon={<Add24Regular />}
                                    disabled={!isEditable || (record.serviceTier !== "premium" && record.serviceTier !== "enterprise")}
                                    onClick={() => setIsAddingApiKey(true)}
                                />
                                <ToolbarButton
                                    aria-label="Revoke"
                                    icon={<PresenceBlockedRegular />}
                                    disabled={!isEditable || (record.serviceTier !== "premium" && record.serviceTier !== "enterprise")}
                                    onClick={onApiKeyRevokation}
                                />
                            </>
                        }
                    />
                </div>
            }

            { !!record.guid &&
                <div className={styles.flexColumn} style={{ borderRadius: "5px", marginTop: "20px" }}>
                    <Subtitle1>Service Tier</Subtitle1>
                    <br />
                    <div className={styles.tierCardContainer}>
                        <div id="trial" className={styles.tierCard} style={ { border: `1px solid ${record.serviceTier === "trial" ? "rgb(85, 214, 170)" : "white" }` } }>
                            <Subtitle2>Trial</Subtitle2>
                            <ul>
                                <li>Free</li>
                                <li>Max 1 customer</li>
                                <li>Max 3 products</li>
                                <li>Free short-lived licenses (valid up to one month)</li>
                                <li>20€ (excl. tax) per license having a validity longer than one month</li>
                            </ul>
                        </div>

                        <div id="basic" className={styles.tierCard} style={ { border: `1px solid ${record.serviceTier === "basic" ? "rgb(85, 214, 170)" : "white" }` } }>
                            <Subtitle2>Basic</Subtitle2>
                            <ul>
                                <li>15€ (excl. tax) / month</li>
                                <li>Unlimited customers</li>
                                <li>Max 3 products</li>
                                <li>Free short-lived licenses (valid up to one month)</li>
                                <li>20€ (excl. tax) per license having a validity longer than one month</li>
                            </ul>
                            { (!record.subscriptionId || record.subscriptionState === "canceled") && (
                                <Button
                                    appearance='primary'
                                    className={styles.purchaseButton}
                                    onClick={() => navigate(`/checkout?priceId=${priceIdDictionary["basic"]}&referenceId=${record.externalId}`)}
                                >
                                    Subscribe
                                </Button>
                            ) }

                            { record.subscriptionId && record.subscriptionState !== "canceled" && record.subscriptionState !== "canceled" && record.serviceTier === "basic" && activeTierButton }
                        </div>

                        <div id="premium" className={styles.tierCard} style={ { border: `1px solid ${record.serviceTier === "premium" ? "rgb(85, 214, 170)" : "white" }` } }>
                            <Subtitle2>Premium</Subtitle2>
                            <ul>
                                <li>500€ (excl. tax) / month</li>
                                <li>Unlimited customers</li>
                                <li>Unlimited products</li>
                                <li>Unlimited licenses without additional costs</li>
                                <li>Telemetry API for usage tracking in customer organizations (limited to one product)</li>
                            </ul>
                            { (!record.subscriptionId || record.subscriptionState === "canceled") && (
                                <Button
                                    appearance='primary'
                                    className={styles.purchaseButton}
                                    onClick={() => navigate(`/checkout?priceId=${priceIdDictionary["premium"]}&referenceId=${record.externalId}`)}
                                >
                                    Subscribe
                                </Button>
                            ) }

                            { record.subscriptionId && record.subscriptionState !== "canceled" && record.serviceTier === "premium" && activeTierButton }
                        </div>

                        <div id="enterprise"
                            className={styles.tierCard}
                            style={ { border: `1px solid ${record.serviceTier === "enterprise" ? "rgb(85, 214, 170)" : "white" }` } }
                        >
                            <Subtitle2>Enterprise</Subtitle2>
                            <ul>
                                <li>1000€ (excl. tax) / month</li>
                                <li>Unlimited customers</li>
                                <li>Unlimited products</li>
                                <li>Unlimited licenses without additional costs</li>
                                <li>Telemetry API for usage tracking in customer organizations (for all products)</li>
                                <li>
                                    <InfoLabel
                                        htmlFor={selfServiceSpanId}
                                        className={styles.infoLabel}
                                        info="Offer landing pages for customers to generate demo licenses."
                                    >
                                        <Text id={selfServiceSpanId}>Customer self-service</Text>
                                    </InfoLabel>
                                </li>
                            </ul>
                            { (!record.subscriptionId || record.subscriptionState === "canceled") && (
                                <Button
                                    appearance='primary'
                                    className={styles.purchaseButton}
                                    onClick={() => navigate(`/checkout?priceId=${priceIdDictionary["enterprise"]}&referenceId=${record.externalId}`)}
                                >
                                    Subscribe
                                </Button>
                            ) }

                            { record.subscriptionId && record.subscriptionState !== "canceled" && record.serviceTier === "enterprise" && activeTierButton }
                        </div>
                    </div>
                </div>
            }
        </>
    ), [styles.infoLabel, styles.flexRow, styles.flexColumn, styles.tierCardContainer, styles.tierCard, styles.purchaseButton, record.name, record.landingPageEnabled, record.guid, record.externalId, record?.users, record?.apiKeys, record.serviceTier, record.subscriptionId, record.subscriptionState, isEditable, onNameChange, onLandingPageEnabledChange, editMember, memberColumns, onMemberSelection, onMemberDeletion, editApiKey, apiKeyColumns, onApiKeySelection, onApiKeyRevokation, activeTierButton, selfServiceSpanId, navigate, priceIdDictionary]);

    return (
        <FormBase
            id={publisherId}
            record={record}
            isSaving={isSaving}
            dialogs={dialogs}
            toolbar={toolbar}
            body={body}
            onSave={submitAndSet}
        />
    );
};
